"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import { usePromoCode } from "@/providers/PromoCodeProvider";
import { useCartActions } from "@/hooks/useCartActions";
import { fetchCheckoutSummary } from "@/services/purchase/purchaseApis";
import { queryKeys } from "@/constants/queries.keys";

interface FormInputs {
  coupon: string;
}

export function usePromoCodeActions() {
  const t = useTranslations("Shared.cartSheet.couponCode");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { promoCodeData, setPromoCodeData } = usePromoCode();
  const { cartItems } = useCartActions();

  const [showCouponCollapsible, setShowCouponCollapsible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<FormInputs>();

  // Single, unified mutation triggers both guest and auth promo validations!
  const { mutate: applyPromoApi, isPending: isPromoApplying } = useMutation({
    mutationFn: async (couponCode: string) => {
      const response = await fetchCheckoutSummary({
        cartItems,
        promoCode: couponCode,
        locale,
        isAuthenticated,
      });

      if (!response.status) {
        throw new Error(response.message);
      }
      return response.data;
    },

    onSuccess: (data) => {
      if (data) {
        const { promo_applied, promo_applied_message } = data.data;
        if (promo_applied) {
          // 1. Save validated promo code globally
          setPromoCodeData(data.data);

          // 2. Invalidate queries to instantly fetch the fresh summary prices
          queryClient.invalidateQueries({
            queryKey: [queryKeys.purchase.checkoutSummary],
          });

          setShowCouponCollapsible(false);
          reset();
        } else {
          toast.error(promo_applied_message || t("validation.notAvailable"));
        }
      }
    },

    onError: (error) => {
      setPromoCodeData(null);
      toast.error(error.message);
    },
  });

  function onSubmit(values: FormInputs) {
    if (promoCodeData) {
      setError("coupon", { message: t(`alreadyAppliedText`) });
      return;
    }
    applyPromoApi(values.coupon);
  }

  function handleRemoveCoupon() {
    setPromoCodeData(null);
    clearErrors();
    // Refresh the checkout summary after removing the coupon code
    queryClient.invalidateQueries({ queryKey: ["checkout-summary"] });
  }

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    isPromoApplying,
    showCouponCollapsible,
    setShowCouponCollapsible,
    promoCodeData,
    handleRemoveCoupon,
  };
}
